import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";
import { cn } from "@/lib/utils";
import {
  Languages as LangIcon, Plus, Pencil, Trash2, Search, Eye, EyeOff, Star,
  RefreshCw, GitMerge, Wand2, X, Loader2, Save,
} from "lucide-react";

const STATUS = [["", "All"], ["active", "Active"], ["inactive", "Inactive"], ["featured", "Featured"], ["deleted", "Deleted"]];

function LangModal({ lang, onClose, onSaved }) {
  const [form, setForm] = useState(lang || { code: "", name_en: "", name_fr: "", native_name: "", description_en: "", description_fr: "", image_url: "", is_active: 1 });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const save = async () => {
    setSaving(true); setErr("");
    try {
      if (lang?.id) await axios.put(`${API}/admin/languages/${lang.id}`, form, { withCredentials: true });
      else await axios.post(`${API}/admin/languages`, form, { withCredentials: true });
      onSaved();
    } catch (e) { setErr(e.response?.data?.detail || "Save failed"); }
    setSaving(false);
  };
  return (
    <div className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-4" data-testid="language-modal">
      <div className="bg-slate-950 border border-slate-800 rounded-2xl w-full max-w-lg">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-800">
          <h3 className="font-bold text-white">{lang?.id ? "Edit Language" : "New Language"}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-white"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3">
          {err && <p className="text-red-400 text-xs">{err}</p>}
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-[11px] text-slate-500">Code (e.g. fra, en)</label>
              <input value={form.code || ""} onChange={e => set("code", e.target.value)} data-testid="lang-code" className="w-full px-3 py-2 bg-slate-900 border border-slate-800 rounded-lg text-white text-sm" /></div>
            <div><label className="text-[11px] text-slate-500">Native Name</label>
              <input value={form.native_name || ""} onChange={e => set("native_name", e.target.value)} data-testid="lang-native" className="w-full px-3 py-2 bg-slate-900 border border-slate-800 rounded-lg text-white text-sm" /></div>
            <div><label className="text-[11px] text-slate-500">Name (EN)</label>
              <input value={form.name_en || ""} onChange={e => set("name_en", e.target.value)} data-testid="lang-name-en" className="w-full px-3 py-2 bg-slate-900 border border-slate-800 rounded-lg text-white text-sm" /></div>
            <div><label className="text-[11px] text-slate-500">Nom (FR)</label>
              <input value={form.name_fr || ""} onChange={e => set("name_fr", e.target.value)} data-testid="lang-name-fr" className="w-full px-3 py-2 bg-slate-900 border border-slate-800 rounded-lg text-white text-sm" /></div>
          </div>
          <div><label className="text-[11px] text-slate-500">Cover Image URL</label>
            <input value={form.image_url || ""} onChange={e => set("image_url", e.target.value)} data-testid="lang-image" className="w-full px-3 py-2 bg-slate-900 border border-slate-800 rounded-lg text-white text-sm" /></div>
        </div>
        <div className="flex justify-end gap-2 px-5 py-3 border-t border-slate-800">
          <button onClick={onClose} className="px-4 py-2 rounded-lg bg-slate-800 text-slate-300 text-sm">Cancel</button>
          <button onClick={save} disabled={saving || !(form.code || form.name_en)} data-testid="lang-save" className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium disabled:opacity-40 flex items-center gap-1.5">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminLanguages() {
  const [langs, setLangs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [modal, setModal] = useState(undefined); // undefined=closed, null=new, obj=edit
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const fetch = useCallback(() => {
    setLoading(true);
    const p = new URLSearchParams({ search, status });
    axios.get(`${API}/admin/languages?${p}`, { withCredentials: true })
      .then(r => { setLangs(r.data.languages || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [search, status]);
  useEffect(() => { fetch(); }, [fetch]);

  const toggle = async (l, field) => {
    await axios.patch(`${API}/admin/languages/${l.id}/toggle`, { field, value: l[field] ? 0 : 1 }, { withCredentials: true });
    fetch();
  };
  const del = async (l) => {
    if (!window.confirm(`Delete "${l.name_en}"?`)) return;
    await axios.delete(`${API}/admin/languages/${l.id}`, { withCredentials: true }); fetch();
  };
  const merge = async (l) => {
    const target = window.prompt(`Merge "${l.name_en}" INTO which language code? (channels are reassigned, nothing deleted)`);
    if (!target) return;
    const tgt = langs.find(x => x.code === target.trim().toLowerCase());
    if (!tgt) { alert("Target code not found"); return; }
    await axios.post(`${API}/admin/languages/${l.id}/merge`, { target_id: tgt.id }, { withCredentials: true }); fetch();
  };
  const syncMaster = async () => {
    setBusy(true); setMsg("");
    try { const r = await axios.post(`${API}/admin/import/languages-master`, {}, { withCredentials: true }); setMsg(`Synced: ${r.data.created} created`); fetch(); }
    catch (e) { setMsg(e.response?.data?.detail || "Sync failed"); }
    setBusy(false);
  };
  const normalize = async () => {
    setBusy(true); setMsg("");
    try {
      const prev = await axios.post(`${API}/admin/languages/normalize`, { apply: false }, { withCredentials: true });
      if (!prev.data.merges.length) { setMsg("No duplicates found."); setBusy(false); return; }
      if (window.confirm(`Merge ${prev.data.merges.length} duplicate language record(s)?`)) {
        const r = await axios.post(`${API}/admin/languages/normalize`, { apply: true }, { withCredentials: true });
        setMsg(`Normalized: ${r.data.merges.length} merged`); fetch();
      }
    } catch (e) { setMsg(e.response?.data?.detail || "Normalize failed"); }
    setBusy(false);
  };

  return (
    <div data-testid="admin-languages">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-2">
          <LangIcon size={24} className="text-blue-400" />
          <h2 className="text-2xl font-bold text-white">Languages</h2>
          <span className="text-xs text-slate-500 ml-1">{langs.length}</span>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={syncMaster} disabled={busy} data-testid="sync-languages-btn" className="px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-slate-300 text-sm hover:text-white flex items-center gap-1.5">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Sync Languages
          </button>
          <button onClick={normalize} disabled={busy} data-testid="normalize-languages-btn" className="px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-slate-300 text-sm hover:text-white flex items-center gap-1.5">
            <Wand2 size={14} /> Normalize
          </button>
          <button onClick={() => setModal(null)} data-testid="add-language-btn" className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-500 flex items-center gap-1.5">
            <Plus size={14} /> Add
          </button>
        </div>
      </div>

      {msg && <p className="text-xs text-slate-400 mb-3" data-testid="lang-msg">{msg}</p>}

      <div className="flex flex-wrap gap-2 mb-4">
        <div className="relative flex-grow max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={14} />
          <input value={search} onChange={e => setSearch(e.target.value)} data-testid="lang-search" placeholder="Search languages..." className="w-full pl-9 pr-3 py-2 bg-slate-900 border border-slate-800 rounded-lg text-white text-sm" />
        </div>
        <div className="flex gap-1">
          {STATUS.map(([v, l]) => (
            <button key={v} onClick={() => setStatus(v)} className={cn("px-3 py-2 rounded-lg text-xs font-medium border", status === v ? "bg-blue-600/20 text-blue-400 border-blue-500/30" : "text-slate-500 border-slate-800 hover:text-white")}>{l}</button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="space-y-2">{Array.from({ length: 5 }).map((_, i) => <div key={i} className="h-12 bg-slate-800/30 rounded-lg animate-pulse" />)}</div>
      ) : langs.length === 0 ? (
        <div className="text-center py-16 text-slate-500" data-testid="lang-empty">No languages yet. Click "Sync Languages" to import from IPTV-ORG, or add one.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="languages-table">
            <thead><tr className="border-b border-slate-800 text-[11px] text-slate-500">
              <th className="text-left py-2 pr-3">Language</th><th className="text-left py-2 pr-3">Native</th>
              <th className="text-left py-2 pr-3">Code</th><th className="text-left py-2 pr-3">Channels</th>
              <th className="text-left py-2 pr-3">Status</th><th className="text-right py-2">Actions</th>
            </tr></thead>
            <tbody>
              {langs.map(l => (
                <tr key={l.id} className="border-b border-slate-800/40 hover:bg-slate-800/20" data-testid={`lang-row-${l.code}`}>
                  <td className="py-2.5 pr-3 text-white font-medium">{l.name_en} <span className="text-slate-500 text-xs">/ {l.name_fr}</span></td>
                  <td className="py-2.5 pr-3 text-slate-400">{l.native_name}</td>
                  <td className="py-2.5 pr-3 text-slate-400 font-mono text-xs">{l.code}</td>
                  <td className="py-2.5 pr-3 text-slate-300">{l.channel_count}</td>
                  <td className="py-2.5 pr-3">
                    <span className={cn("text-[10px] px-2 py-0.5 rounded-full border", l.is_active ? "bg-green-500/15 text-green-400 border-green-500/30" : "bg-slate-500/15 text-slate-400 border-slate-500/30")}>{l.is_active ? "Active" : "Inactive"}</span>
                  </td>
                  <td className="py-2.5 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => toggle(l, "is_featured")} title="Feature" className={cn("p-1.5 rounded-lg hover:bg-slate-800", l.is_featured ? "text-amber-400" : "text-slate-500")} data-testid={`lang-feature-${l.code}`}><Star size={13} /></button>
                      <button onClick={() => toggle(l, "is_active")} title="Toggle active" className="p-1.5 rounded-lg text-slate-500 hover:text-white hover:bg-slate-800" data-testid={`lang-toggle-${l.code}`}>{l.is_active ? <Eye size={13} /> : <EyeOff size={13} />}</button>
                      <button onClick={() => merge(l)} title="Merge" className="p-1.5 rounded-lg text-slate-500 hover:text-white hover:bg-slate-800"><GitMerge size={13} /></button>
                      <button onClick={() => setModal(l)} title="Edit" className="p-1.5 rounded-lg text-slate-500 hover:text-white hover:bg-slate-800" data-testid={`lang-edit-${l.code}`}><Pencil size={13} /></button>
                      <button onClick={() => del(l)} title="Delete" className="p-1.5 rounded-lg text-red-400/60 hover:text-red-400 hover:bg-red-500/10"><Trash2 size={13} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modal !== undefined && <LangModal lang={modal} onClose={() => setModal(undefined)} onSaved={() => { setModal(undefined); fetch(); }} />}
    </div>
  );
}
